import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { showErrorToast, showSuccessToast } from '../../../utils/toastUtils';
import type { DocumentDto, ManualLoadDocumentDto } from '../../generated/client';
import { createDocument } from '../../generated/client';
import { queryKeys } from '../../query-keys';

export const useCreateDocument = (): UseMutationResult<DocumentDto, AxiosError, DocumentDto> => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (document) => {
            if (!document.documentFiles) {
                throw new Error('No document files provided');
            }
            const enFile = document.documentFiles.find((f) => f.language === 'EN')?.file;
            const deFile = document.documentFiles.find((f) => f.language === 'DE')?.file;
            const frFile = document.documentFiles.find((f) => f.language === 'FR')?.file;
            const itFile = document.documentFiles.find((f) => f.language === 'IT')?.file;

            return createDocument({
                body: {
                    enFile: enFile!,
                    deFile: deFile!,
                    frFile: frFile!,
                    itFile: itFile!,
                    document: new Blob([JSON.stringify(document)], { type: 'application/json' }) as unknown as ManualLoadDocumentDto,
                },
            }).then((result) => result.data!);
        },
        onSuccess: (savedDocument) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.documents() });
            showSuccessToast(t('successActionGeneric'));
            navigate({
                pathname: '/content-admin/documents',
                search: `?document=${savedDocument.id}`,
            });
        },
        onError: (error) => showErrorToast({ error, userMessage: t('failureActionGeneric') }),
    });
};
